import { Component, Input, OnInit } from '@angular/core';
import { ChartType } from 'chart.js';
import {
  chartColors,
  RtoBarChartConstants,
  ToastTitle,
} from '@constants/global.enum';
import { DatePipe } from '@angular/common';
import { QuarterlyFeedbackComplianceService } from '@services/quarterly-feedback-compliance.service';
import { Subject } from 'rxjs';
import { PEOPLE_METRICS } from '@constants/people-metrics.constants';
import { ToasterService } from '@services/toaster.service';
import { ErrorMessages } from '@constants/constants';
import { RTOComplianceResponse } from '@interfaces/quarterly-feedback.model';
import { yearDateFormat } from '@constants/dateFormat';
@Component({
  selector: 'app-rto-bar-chart',
  templateUrl: './rto-bar-chart.component.html',
  styleUrls: ['./rto-bar-chart.component.scss'],
})
export class RtoBarChartComponent implements OnInit {
  @Input() vendorId: string | null = null;
  private readonly destroy$ = new Subject<void>();
  barChartLabels: string[] = [];
  barChartData: any[] = [];
  barChartOptions: any = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: true,
        position: RtoBarChartConstants.legendPosition,
        labels: {
          usePointStyle: true,
          font: {
            size: RtoBarChartConstants.legendFontSize,
          },
          color: chartColors.textDark,
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: chartColors.tooltipBackground,
        titleColor: chartColors.tooltipTitle,
        bodyColor: chartColors.tooltipBody,
        borderColor: chartColors.dividerGrey,
        borderWidth: RtoBarChartConstants.toolTipBorderWidth,
      },
      datalabels: {
        color: chartColors.backgroundWhite,
        anchor: RtoBarChartConstants.dataLabelAnchor,
        align: RtoBarChartConstants.dataLabelAlign,
        font: {
          size: RtoBarChartConstants.dataLabelFontSize,
        },
      },
    },
    hover: {
      mode: null,
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
          drawTicks: true,
        },
        ticks: {
          display: true,
          align: RtoBarChartConstants.tickAlign,
          font: {
            size: RtoBarChartConstants.tickFontSize,
            color: chartColors.textDark,
          },
        },
        barThickness: RtoBarChartConstants.xBarThickness,
      },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: {
          display: false,
          color: chartColors.dividerGrey,
          drawTicks: true,
        },
        ticks: {
          display: true,
          align: RtoBarChartConstants.tickAlign,
          font: {
            size: RtoBarChartConstants.tickFontSize,
            color: chartColors.textDark,
          },
          stepSize: RtoBarChartConstants.yTickStepSize,
        },
      },
    },
  };
  chartType: ChartType = 'bar';
  constructor(
    private toasterService: ToasterService,
    private quarterlyFeedbackComplianceService: QuarterlyFeedbackComplianceService,
  ) {}

  ngOnInit(): void {
    this.loadRtoComplianceData();
  }

  private loadRtoComplianceData(): void {
    if (!this.vendorId) {
      this.toasterService.showError(
        PEOPLE_METRICS.MESSAGES.MISSING_IDS_ERROR,
        ToastTitle.ERROR,
      );
      return;
    }

    this.quarterlyFeedbackComplianceService
      .getRtoCompliance(this.vendorId, '')
      .subscribe({
        next: (response: { data: RTOComplianceResponse }) => {
          this.generateYearlyData(response.data);
        },
        error: (error) => {
          this.showError(ErrorMessages.NoDataAvailable);
        },
      });
  }

   generateYearlyData(rtoData: RTOComplianceResponse): void {
    const now = new Date();
    const labels: string[] = [];
    const compliantData: number[] = new Array(12).fill(0);
    const nonCompliantData: number[] = new Array(12).fill(0);

    const datePipe = new DatePipe('en-US');
    for (let i = 1; i <= 12; i++) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() - i);
      const monthLabel = datePipe.transform(monthDate, yearDateFormat.MonthYear);
      if (monthLabel) {
      labels.unshift(monthLabel);
      }
    }
    if (rtoData && Array.isArray(rtoData)) {
      rtoData?.forEach((item: any) => {
        const dataDate = new Date(
          item.attendanceYear,
          item.attendanceMonth - 1,
        );

        const monthLabel = datePipe.transform(dataDate, yearDateFormat.MonthYear);
        const monthIndex = labels.indexOf(monthLabel as string);

        if (monthIndex !== -1) {
          if (item.complianceStatus === RtoBarChartConstants.barChartLabelCompliant) {
            compliantData[monthIndex]++;
          } else if (item.complianceStatus === RtoBarChartConstants.barChartLabelNonCompliant) {
            nonCompliantData[monthIndex]++;
          }
        }
      });
    }

    this.barChartLabels = labels;
    this.barChartData = [
      {
        data: compliantData,
        label: RtoBarChartConstants.barChartLabelCompliant,
        backgroundColor: chartColors.mintGreen,
      },
      {
        data: nonCompliantData,
        label: RtoBarChartConstants.barChartLabelNonCompliant,
        backgroundColor: chartColors.darkOrange,
      },
    ];
  }
  private showError(message: string): void {
    this.toasterService.showError(message, ToastTitle.ERROR);
  }
}
